import type { HeroSectionContent } from "@site-factory/config";
import { cn } from "../utils";
import { getHeroTheme } from "../utils/heroTheme";
import { Button } from "../components/Button";
import { MainContainer } from "../layout/MainContainer";
import { SectionShell } from "../components/SectionShell";
import { DecorativeBlob } from "../components/DecorativeBlob";
import { BackgroundPattern } from "../components/BackgroundPattern";
import type { ReactNode } from "react";

export interface HeroSectionProps extends Omit<HeroSectionContent, "ctaText" | "ctaHref" | "subheadline"> {
  ctaText?: string;
  ctaHref?: string;
  subheadline?: string;
  className?: string;
  sectionClassName?: string;
  contentClassName?: string;
  minHeightClassName?: string;
  backgroundMode?: "gradient" | "solid";
  actions?: ReactNode;
  showCta?: boolean;
  align?: "center" | "left";
  showSideCard?: boolean;
  layout?: "hero" | "banner";
}

export function HeroSection({
  eyebrow,
  headline,
  subheadline,
  ctaText,
  ctaHref,
  secondaryCta,
  ctaNote,
  backgroundImage,
  mascotImage,
  backgroundVideo,
  variant = "centered",
  backgroundVariant = "soft",
  tone = "light",
  backgroundMode = "gradient",
  actions,
  showCta = true,
  align,
  showSideCard,
  layout = "hero",
  className,
  sectionClassName,
  contentClassName,
  minHeightClassName,
}: HeroSectionProps) {
  const isBanner = layout === "banner";
  const isCentered = isBanner ? false : (align ? align === "center" : variant === "centered");
  const shouldShowSideCard = showSideCard ?? !isCentered;
  const theme = getHeroTheme({ tone, backgroundVariant, backgroundMode });
  const useDarkTheme = theme.isDark || Boolean(backgroundImage);
  const isBoldLight = !useDarkTheme && backgroundVariant === "bold";
  const minHeightClasses = minHeightClassName ?? "min-h-[72vh] md:min-h-[80vh]";
  const shouldShowCta = showCta && Boolean(ctaText && ctaHref);

  const playfulDecor = isBoldLight;
  const backgroundStyle = backgroundImage ? undefined : theme.backgroundStyle;

  const content = (
    <div className={cn("space-y-6", isCentered ? "mx-auto" : "max-w-2xl text-left")}>
      {eyebrow && (
        <div
          className={cn(
            "inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide",
            theme.eyebrowClasses
          )}
        >
          <span className="h-1.5 w-1.5 rounded-full bg-current" />
          {eyebrow}
        </div>
      )}
      <h1
        className={cn(
            "text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight",
            theme.titleClasses
          )}
        >
          {headline}
        </h1>
      {subheadline && (
        <p
          className={cn(
            "text-lg md:text-xl leading-relaxed",
            theme.subtitleClasses
          )}
        >
          {subheadline}
        </p>
      )}
      {actions ? (
        <div
          className={cn(
            "flex gap-4 mt-6 flex-wrap",
            isCentered ? "justify-center" : "justify-start"
          )}
        >
          {actions}
        </div>
      ) : shouldShowCta ? (
        <div
          className={cn(
            "flex gap-4 mt-6 flex-wrap",
            isCentered ? "justify-center" : "justify-start"
          )}
        >
          <Button asChild variant="primary" size="lg">
            <a href={ctaHref}>{ctaText}</a>
          </Button>
                {secondaryCta && (
                  <Button
                    asChild
                    variant="outline"
                    size="lg"
                    className={theme.outlineButtonClasses}
                  >
                    <a href={secondaryCta.href}>{secondaryCta.text}</a>
                  </Button>
                )}
              </div>
            ) : null}
      {ctaNote && (
        <div
          className={cn(
            "inline-flex items-center gap-2 text-sm font-medium",
            theme.ctaNoteClasses,
            isCentered ? "justify-center mx-auto" : "justify-start"
          )}
        >
          <span className="h-2 w-2 rounded-full bg-accent" />
          {ctaNote}
        </div>
      )}
    </div>
  );

  return (
    <SectionShell
      sectionClassName={cn(
        "relative overflow-hidden flex items-center",
        minHeightClasses,
        backgroundImage && "bg-cover bg-center",
        sectionClassName
      )}
      paddingClassName="py-0"
      style={backgroundStyle}
    >
      {/* Background layers */}
      {backgroundVideo ? (
        <div className="absolute inset-0 overflow-hidden">
          <video
            className="h-full w-full object-cover"
            autoPlay={backgroundVideo.autoplay ?? true}
            loop={backgroundVideo.loop ?? true}
            muted={backgroundVideo.mute ?? true}
            playsInline
            poster={backgroundVideo.poster ?? backgroundVideo.mobilePoster}
          >
            <source src={backgroundVideo.src} type={backgroundVideo.type ? `video/${backgroundVideo.type}` : undefined} />
          </video>
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-background/70" aria-hidden="true" />
        </div>
      ) : backgroundImage ? (
        <div className="absolute inset-0">
          <img src={backgroundImage} alt="" className="h-full w-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-background" aria-hidden="true" />
        </div>
      ) : (
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          {/* Background pattern */}
          <BackgroundPattern variant="dots" opacity="light" color="primary" />

          {/* Animated decorative blobs */}
          <DecorativeBlob variant="accent" size="xl" position="-left-24 -top-24" animation="pulse-glow" />
          <DecorativeBlob variant="primary" size="xl" position="right-[-12%] bottom-[-12%]" animation="float" />

          {!isBoldLight && (
            <>
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_30%,rgba(255,255,255,0.06),transparent_45%)]" />
              <div className="absolute inset-0 bg-[linear-gradient(120deg,rgba(255,255,255,0.05),transparent_45%,rgba(255,255,255,0.12)_80%)] mix-blend-screen opacity-60" />
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(255,255,255,0.04),transparent_35%)]" />
            </>
          )}
          {playfulDecor && (
            <>
              <DecorativeBlob variant="secondary" size="md" position="-top-10 left-8" animation="pulse-glow" />
              <DecorativeBlob variant="accent" size="md" position="top-12 right-16" animation="float" />
              <DecorativeBlob variant="primary" size="lg" position="bottom-[-6%] left-[35%]" animation="pulse-glow" />
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_60%_0%,rgba(255,255,255,0.12),transparent_45%)] opacity-70" />
            </>
          )}
        </div>
      )}

      {/* Floating Mascot Image. z-20 keeps it below the sticky header (z-50) so
          it doesn't slide over the nav on scroll, while staying above banner
          content (z-10). */}
      {mascotImage && (
        <div className="absolute right-8 bottom-8 md:right-12 md:bottom-12 z-20 pointer-events-none">
          <div className="relative animate-float">
            <img
              src={mascotImage}
              alt="Bizzy Mascot"
              className="w-28 h-28 md:w-36 md:h-36 lg:w-44 lg:h-44 object-contain drop-shadow-2xl"
            />
          </div>
        </div>
      )}

      <MainContainer className={cn("relative z-10 w-full", contentClassName, className)}>
        {layout === "banner" ? (
          <div className="w-full max-w-3xl space-y-4 text-left">
            {content}
          </div>
        ) : (
          <div
            className={cn(
              "grid gap-12 items-center",
              isCentered
                ? "max-w-4xl mx-auto text-center"
                : shouldShowSideCard
                  ? "md:grid-cols-[1.05fr_0.95fr] justify-items-start text-left"
                  : "max-w-3xl justify-items-start mx-0 text-left"
            )}
          >
            {content}

            {shouldShowSideCard && (
              <div className="relative">
                <div className="absolute inset-4 rounded-[24px] bg-black/10 blur-3xl" aria-hidden="true" />
                <div
                  className={cn(
                    "relative overflow-hidden rounded-[20px] border shadow-lg",
                    useDarkTheme
                      ? "bg-white/5 border-white/10 backdrop-blur-sm"
                      : "bg-white/80 border-white/60"
                  )}
                >
                  <div
                    className="absolute inset-0 bg-[radial-gradient(circle_at_30%_30%,rgba(255,255,255,0.18),transparent_45%)] opacity-70"
                    aria-hidden="true"
                  />
                  <div className="relative p-6 space-y-3">
                    <div className="text-sm font-semibold uppercase tracking-wide text-primary/80">
                      Snapshot
                    </div>
                    <p
                      className={cn(
                        "text-base leading-relaxed",
                        useDarkTheme ? "text-white/80" : "text-neutral-700"
                      )}
                    >
                      {subheadline}
                    </p>
                    <div className="flex items-center gap-2 text-sm font-semibold text-primary">
                      <span className="h-2 w-2 rounded-full bg-accent" />
                      {ctaText}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </MainContainer>
    </SectionShell>
  );
}
